type CurrentUserEmail = string | string[];

type BuildReplyAllRecipientsParams = {
  from?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  currentUserEmail?: CurrentUserEmail;
};

type BuildReplyRecipientsParams = {
  from?: string;
  currentUserEmail?: CurrentUserEmail;
};

type ReplyAllRecipients = {
  to: string;
  cc: string;
  bcc: string;
};

type ReplyRecipients = {
  to: string;
};

function extractEmails(raw?: string): string[] {
  if (!raw) return [];

  const matches = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi);
  if (!matches) return [];

  return matches.map((email) => email.trim()).filter(Boolean);
}

function dedupeEmailsPreservingOrder(emails: string[]): string[] {
  const seenByLower = new Map<string, string>();

  emails.forEach((email) => {
    const normalized = email.toLowerCase();
    if (!seenByLower.has(normalized)) {
      seenByLower.set(normalized, email);
    }
  });

  return Array.from(seenByLower.values());
}

function removeCurrentUser(
  emails: string[],
  currentUserEmail?: CurrentUserEmail
): string[] {
  const rawCurrentUserEmails = (
    Array.isArray(currentUserEmail) ? currentUserEmail : [currentUserEmail]
  )
    .map((value) => (value || "").trim())
    .filter(Boolean);
  const excludedSet = new Set(
    rawCurrentUserEmails
      .flatMap((value) => [value, ...extractEmails(value)])
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
  );
  if (excludedSet.size === 0) return emails;

  return emails.filter((email) => !excludedSet.has(email.toLowerCase()));
}

function normalizeForReply(
  emails: string[],
  currentUserEmail?: CurrentUserEmail
): string[] {
  return removeCurrentUser(
    dedupeEmailsPreservingOrder(emails),
    currentUserEmail
  );
}

export function buildReplyRecipients({
  from,
  currentUserEmail
}: BuildReplyRecipientsParams): ReplyRecipients {
  const sender = extractEmails(from);
  const toUnique = normalizeForReply(sender, currentUserEmail);

  return {
    to: toUnique.join(",")
  };
}

export function buildReplyAllRecipients({
  from,
  to,
  cc,
  bcc,
  currentUserEmail
}: BuildReplyAllRecipientsParams): ReplyAllRecipients {
  const sender = extractEmails(from);
  const originalTo = extractEmails(to);
  const originalCc = extractEmails(cc);
  const originalBcc = extractEmails(bcc);

  // Regla "Responder a todos" (GDC-5211):
  // - To: remitente + destinatarios principales originales (sin duplicados, sin mi propio email)
  // - CC: copiados originales (sin duplicados, sin mi propio email, y sin repetir lo ya presente en To)
  // El filtrado de "mi email" aplica también al remitente: si respondo mi propio email,
  // no me incluyo en el Para. Si eso deja el Para vacío, queda vacío (no se responde a uno mismo).
  const senderFiltered = normalizeForReply(sender, currentUserEmail);
  const originalToFiltered = normalizeForReply(originalTo, currentUserEmail);
  const toUnique = dedupeEmailsPreservingOrder([
    ...senderFiltered,
    ...originalToFiltered
  ]);
  const toSet = new Set(toUnique.map((email) => email.toLowerCase()));

  const ccUnique = normalizeForReply(originalCc, currentUserEmail).filter(
    (email) => !toSet.has(email.toLowerCase())
  );
  const ccSet = new Set(ccUnique.map((email) => email.toLowerCase()));

  const bccUnique = normalizeForReply(originalBcc, currentUserEmail).filter(
    (email) =>
      !toSet.has(email.toLowerCase()) && !ccSet.has(email.toLowerCase())
  );

  return {
    to: toUnique.join(","),
    cc: ccUnique.join(","),
    bcc: bccUnique.join(",")
  };
}
